/**
 * useProjectOutreach.ts — Sprint 7E
 * Hooks de outreach para proyectos. Espejo del patrón de useLeads outreach hooks.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';

// ── Tipos ─────────────────────────────────────────────────────────────────────

export interface ProjectOutreachDraft {
  id: string;
  projectId: number;
  contactId: number;
  sequenceOrder: number;
  draftType: 'new' | 'followup' | 'reply' | 'brief_request' | 'content_plan_review' | 'design_review';
  subject: string;
  bodyHtml: string;
  bodyText: string;
  status: 'pending_review' | 'scheduled' | 'sent' | 'cancelled' | 'failed' | 'skipped';
  cancellationReason: string | null;
  sendError: string | null;
  sendAttemptedAt: number | null;
  inReplyToMessageId: string | null;
  scheduledFor: number | null;
  sentAt: number | null;
  sentMessageId: string | null;
  createdAt: number;
  updatedAt: number;
  generatedByClaude: number;
  claudeCostUsd: number | null;
}

export interface ProjectOutreachReply {
  id: string;
  projectId: number;
  contactId: number;
  fromAddress: string;
  subject: string | null;
  bodyHtml: string | null;
  bodyText: string | null;
  receivedAt: number;
  messageId: string;
  inReplyTo: string | null;
  matchedDraftId: string | null;
  createdAt: number;
}

export interface ProjectClientEvent {
  id: number;
  projectId: number;
  stage: string;
  respondedAt: number;
}

// ── Queries ───────────────────────────────────────────────────────────────────

export function useProjectDrafts(projectId: number | null) {
  return useQuery<{ drafts: ProjectOutreachDraft[] }>({
    queryKey: ['project-outreach-drafts', projectId],
    queryFn: () => api.get<{ drafts: ProjectOutreachDraft[] }>(`/api/projects/${projectId}/outreach/drafts`),
    enabled: projectId !== null,
  });
}

export function useProjectReplies(projectId: number | null) {
  return useQuery<{ replies: ProjectOutreachReply[]; clientEvents: ProjectClientEvent[] }>({
    queryKey: ['project-outreach-replies', projectId],
    queryFn: () => api.get<{ replies: ProjectOutreachReply[]; clientEvents: ProjectClientEvent[] }>(`/api/projects/${projectId}/outreach/replies`),
    enabled: projectId !== null,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────────

export function useComposeProjectNew() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, hint }: { projectId: number; hint?: string }) =>
      api.post<{ draftId: string; subject: string; bodyHtml: string; bodyText: string; costUsd: number }>(
        `/api/projects/${projectId}/outreach/compose-new`,
        hint ? { hint } : {},
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', vars.projectId] });
    },
  });
}

export function useComposeProjectFollowup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, hint }: { projectId: number; hint?: string }) =>
      api.post<{ draftId: string; subject: string; bodyHtml: string; bodyText: string; costUsd: number }>(
        `/api/projects/${projectId}/outreach/compose-followup`,
        hint ? { hint } : {},
      ),
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', vars.projectId] });
    },
  });
}

export function useComposeProjectReply(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ replyId, bodyHtml, bodyText }: { replyId: string; bodyHtml: string; bodyText: string }) =>
      api.post<{ draftId: string; subject: string; bodyHtml: string; bodyText: string }>(
        `/api/projects/${projectId}/outreach/replies/${replyId}/compose-reply`,
        { bodyHtml, bodyText },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useUpdateProjectDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, subject, bodyHtml, bodyText }: { draftId: string; subject?: string; bodyHtml?: string; bodyText?: string }) =>
      api.patch<ProjectOutreachDraft>(
        `/api/projects/${projectId}/outreach/drafts/${draftId}`,
        { subject, bodyHtml, bodyText },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useSendProjectDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      api.post<{ ok: boolean; messageId: string }>(`/api/projects/${projectId}/outreach/drafts/${draftId}/send`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useScheduleProjectDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, scheduledFor, delayDays }: { draftId: string; scheduledFor?: string; delayDays?: number }) =>
      api.post<{ ok: boolean; scheduledFor: number }>(
        `/api/projects/${projectId}/outreach/drafts/${draftId}/schedule`,
        { ...(scheduledFor ? { scheduledFor } : {}), ...(delayDays !== undefined ? { delayDays } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useCancelProjectDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId, reason }: { draftId: string; reason?: string }) =>
      api.post<{ ok: boolean }>(`/api/projects/${projectId}/outreach/drafts/${draftId}/cancel`, reason ? { reason } : {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useRegenerateProjectDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      api.post<{ draftId: string; subject: string; bodyHtml: string; bodyText: string; costUsd: number }>(
        `/api/projects/${projectId}/outreach/drafts/${draftId}/regenerate`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useSendProjectManualEmail(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, bodyHtml, bodyText, inReplyTo, references }: { subject: string; bodyHtml: string; bodyText: string; inReplyTo?: string; references?: string[] }) =>
      api.post<{ ok: boolean; messageId?: string }>(
        `/api/projects/${projectId}/outreach/send-manual`,
        { subject, bodyHtml, bodyText, ...(inReplyTo ? { inReplyTo, references: references ?? [inReplyTo] } : {}) },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useCreateManualDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ subject, bodyHtml, bodyText }: { subject: string; bodyHtml: string; bodyText: string }) =>
      api.post<{ draftId: string; subject: string }>(
        `/api/projects/${projectId}/outreach/drafts/manual`,
        { subject, bodyHtml, bodyText },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useSkipProjectDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (draftId: string) =>
      api.post<{ ok: boolean }>(`/api/projects/${projectId}/outreach/drafts/${draftId}/skip`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useFollowupProjectDraft(projectId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ draftId }: { draftId: string }) =>
      api.post<{ draftId: string; subject: string; bodyHtml: string; bodyText: string; costUsd: number }>(
        `/api/projects/${projectId}/outreach/drafts/${draftId}/followup`,
        {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useComposeBriefEmail(projectId: number) {
  const qc = useQueryClient();
  return useMutation<{ draftId: string; existing?: boolean }, Error>({
    mutationFn: () =>
      api.post<{ draftId: string; existing?: boolean }>(`/api/projects/${projectId}/brief/compose-send`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useComposeContentPlanEmail(projectId: number) {
  const qc = useQueryClient();
  return useMutation<{ draftId: string; existing?: boolean }, Error>({
    mutationFn: () =>
      api.post<{ draftId: string; existing?: boolean }>(`/api/projects/${projectId}/content-plan/compose-send`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useComposeDesignEmail(projectId: number) {
  const qc = useQueryClient();
  return useMutation<{ draftId: string; existing?: boolean }, Error>({
    mutationFn: () =>
      api.post<{ draftId: string; existing?: boolean }>(`/api/projects/${projectId}/design/compose-send`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['project-outreach-drafts', projectId] });
    },
  });
}

export function useMarkClientApproved(projectId: number) {
  const qc = useQueryClient();
  return useMutation<{ id: number; slug: string }, Error>({
    mutationFn: () =>
      api.post<{ id: number; slug: string }>(`/api/projects/${projectId}/mark-client-approved`, {}),
    onSuccess: (data) => {
      void qc.invalidateQueries({ queryKey: ['projects'] });
      void qc.invalidateQueries({ queryKey: ['project', data.id] });
      void qc.invalidateQueries({ queryKey: ['project', data.slug] });
    },
  });
}

// ── Client interactions ───────────────────────────────────────────────────────

export interface ClientInteraction {
  id: number;
  projectId: number;
  stage: string;
  token: string;
  status: string;
  sentAt: number;
  respondedAt: number | null;
  responseData: string | null;
  expiresAt: number | null;
}

export function useClientInteractions(projectId: number) {
  return useQuery<ClientInteraction[]>({
    queryKey: ['project-client-interactions', projectId],
    queryFn: () => api.get<ClientInteraction[]>(`/api/projects/${projectId}/client-interactions`),
  });
}
